import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  Modal,
  notification,
  Space,
  Select,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { setSideTab } from "../../../toolkit/slices/inventories/inventorySlice";
import accessCheckPromise from "../../../../utils/accessCheck";
import { logoutUser } from "../../../toolkit/slices/users/userSlice";
import AuthorizationError from "../AuthorizationError";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const DEBOUNCE_DELAY = 400;

const getAuthHeaders = () => {
  const token = window.localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    authorization: `Token ${token}`,
  };
};

const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const SalesmanRegisterContainer = () => {
  const [access, setAccess] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const fetchList = async (page = 1, limit = 50, searchTerm = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (searchTerm) params.set("search", searchTerm);
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/listSalesPersons?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: getAuthHeaders(),
        }
      );
      const data = await res.json();
      if (data.success) {
        setList(Array.isArray(data.data) ? data.data : []);
        setPagination((prev) => ({
          ...prev,
          current: data.pagination?.page ?? page,
          pageSize: data.pagination?.limit ?? limit,
          total: data.pagination?.total ?? 0,
        }));
      } else {
        notification.error({
          message: data.message || "Failed to load sales persons",
        });
        setList([]);
      }
    } catch (err) {
      notification.error({
        message: err?.message || "Failed to load sales persons",
      });
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setSideTab(window.location.pathname));
    accessCheckPromise()
      .then((res) => setAccess(res))
      .catch(() => dispatch(logoutUser()));
  }, [dispatch]);

  // Fetch cities and states using GitHub cities JSON (same as ClientManagement)
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/nshntarora/Indian-Cities-JSON/master/cities.json"
        );
        const citiesData = await response.json();
        setCities(citiesData);

        const uniqueStates = [
          ...new Set(citiesData.map((city) => city.state)),
        ].sort();
        setStates(uniqueStates);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    if (!access) return;
    fetchList(pagination.current, pagination.pageSize);
  }, [access]);

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize || prev.pageSize,
    }));
    fetchList(
      newPagination.current,
      newPagination.pageSize || pagination.pageSize
    );
  };

  const openCreate = () => {
    setModalMode("create");
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = async (record) => {
    const id = record._id;
    if (!id) return;
    setModalMode("edit");
    setEditingId(id);
    setFormLoading(true);
    setModalOpen(true);
    form.resetFields();
    try {
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getSalesPersonById?salesPersonId=${encodeURIComponent(id)}`,
        {
          method: "GET",
          credentials: "include",
          headers: getAuthHeaders(),
        }
      );
      const data = await res.json();
      if (data.success && data.data) {
        form.setFieldsValue({
          name: data.data.name ?? "",
          email: data.data.email ?? "",
          city: data.data.city ?? "",
          state: data.data.state ?? "",
        });
      } else {
        notification.error({
          message: data.message || "Failed to load sales person",
        });
        setModalOpen(false);
      }
    } catch (err) {
      notification.error({
        message: err?.message || "Failed to load sales person",
      });
      setModalOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const name = values.name?.trim();
      if (!name) {
        notification.warning({ message: "Name is required" });
        return;
      }
      setFormLoading(true);
      if (modalMode === "create") {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/createSalesPerson`,
          {
            method: "POST",
            credentials: "include",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              name,
              email: values.email?.trim() || undefined,
              city: values.city?.trim() || undefined,
              state: values.state?.trim() || undefined,
            }),
          }
        );
        const data = await res.json();
        if (data.success) {
          notification.success({
            message: data.message || "Sales person created successfully",
          });
          setModalOpen(false);
          fetchList(pagination.current, pagination.pageSize);
        } else {
          notification.error({
            message: data.message || "Failed to create",
          });
        }
      } else {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/updateSalesPerson`,
          {
            method: "PUT",
            credentials: "include",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              salesPersonId: editingId,
              name,
              email: values.email?.trim() || undefined,
              city: values.city?.trim() || undefined,
              state: values.state?.trim() || undefined,
            }),
          }
        );
        const data = await res.json();
        if (data.success) {
          notification.success({
            message: data.message || "Sales person updated successfully",
          });
          setModalOpen(false);
          fetchList(pagination.current, pagination.pageSize);
        } else {
          notification.error({
            message: data.message || "Failed to update",
          });
        }
      }
    } catch (info) {
      if (info?.errorFields) return;
      notification.error({
        message: info?.message || "Validation failed",
      });
    } finally {
      setFormLoading(false);
    }
  };

  // Auto-select state based on selected city
  const handleCityChange = (cityName) => {
    if (cityName) {
      const selectedCity = cities.find((city) => city.name === cityName);
      if (selectedCity) {
        form.setFieldsValue({ state: selectedCity.state });
      }
    } else {
      form.setFieldsValue({ state: undefined });
    }
  };

  const handleDelete = (record) => {
    const id = record._id;
    if (!id) return;
    Modal.confirm({
      title: "Delete Sales Person",
      content: `Are you sure you want to delete "${record.name || "this sales person"}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setDeleteConfirmId(id);
        try {
          const res = await fetch(
            `${apiBaseUrl}/inventoryManagement/deleteSalesPerson?salesPersonId=${encodeURIComponent(id)}`,
            {
              method: "DELETE",
              credentials: "include",
              headers: getAuthHeaders(),
            }
          );
          const data = await res.json();
          if (data.success) {
            notification.success({
              message: data.message || "Sales person deleted successfully",
            });
            fetchList(pagination.current, pagination.pageSize);
          } else {
            notification.error({
              message: data.message || "Failed to delete",
            });
          }
        } catch (err) {
          notification.error({
            message: err?.message || "Failed to delete",
          });
        } finally {
          setDeleteConfirmId(null);
        }
      },
    });
  };

  if (!access) {
    return <AuthorizationError module="Salesman Register" />;
  }


  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 140,
      align: "center",
      ellipsis: true,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      align: "center",
      ellipsis: true,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 120,
      align: "center",
      ellipsis: true,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 120,
      align: "center",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={deleteConfirmId === record._id}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: "24px" }}>Salesman Register</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Sales Person
        </Button>
      </div>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={list}
        loading={loading}
        components={components}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 700 }}
      />

      <Modal
        title={modalMode === "create" ? "Add Sales Person" : "Edit Sales Person"}
        open={modalOpen}
        onOk={handleFormSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={formLoading}
        destroyOnClose
        okText={modalMode === "create" ? "Create" : "Update"}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
          disabled={formLoading && modalMode === "edit"}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: "Name is required" },
              {
                validator: (_, value) =>
                  value && !value.trim()
                    ? Promise.reject("Name cannot be empty")
                    : Promise.resolve(),
              },
            ]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input placeholder="Enter email" type="email" />
          </Form.Item>
          <Form.Item name="city" label="City">
            <Select
              placeholder="Select city"
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              onChange={handleCityChange}
            >
              {cities.map((city) => (
                <Select.Option
                  key={city.id || `${city.name}-${city.state}`}
                  value={city.name}
                >
                  {city.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="state" label="State">
            <Input disabled />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesmanRegisterContainer;
